import { useCallback, useEffect, useRef, useState } from 'react';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';
import {
  captureException,
  captureRecaptchaException,
} from '../../../utilities/analytics/error-reporting';
import { useScoreRecaptchaScripts } from './recaptcha-service';
import { RecaptchaError } from '../../../utilities/RecaptchaError';
import { RecaptchaConfig } from './types';

const RECAPTCHA_ENTERPRISE_EXECUTE_UNAVAILABLE =
  'Failed to invoke grecaptcha execute function as it is not ready.';

type TrackingEventErrorType = 'recaptcha-window-error' | 'recaptcha-script-error';
type TrackingEventAction = 'loaded-recaptcha' | 'load-recaptcha-failed' | 'get-recaptcha-token';

export interface ScoreRecaptchaResult {
  /**
   * A state that indicates if reCAPTCHA has been loaded to the DOM. Default to
   * `false` and will turn to `true` when it finishes loading. May never become
   * `true` if reCAPTCHA fails to load.
   */
  isLoaded: boolean;

  /**
   * A function that takes an `action` as an argument and returns a reCAPTCHA
   * token asynchronously. This function throws an error if it is called when
   * reCAPTCHA has not been loaded yet. An `action` describes a feature that
   * reCAPTCHA protects. For example, "log_in", "invite_user", "grant_role" are
   * all valid actions. An `action` is also used to differentiate multiple
   * features being protected by the same reCAPTCHA site key.
   */
  fetchRecaptchaToken: (action: string) => Promise<string>;

  /**
   * If reCAPTCHA fails to load, this will be a string containing the reason and
   * `isLoaded` will remain `false`.
   */
  loadRecaptchaErrorReason?: string;

  /**
   * If reCAPTCHA is enabled and whether other values are expected to return real or no-op
   * values. When disabled, the recaptcha script will not be injected into the dom and
   * its dependencies loaded.
   */
  enabled: boolean;
}

export const useScoreRecaptcha = (config: RecaptchaConfig): ScoreRecaptchaResult => {
  const [loadRecaptchaErrorReason, setLoadRecaptchaErrorReason] = useState<string>();
  const [isLoaded, setIsLoaded] = useState(false);
  const scriptInitializationRef = useRef<Promise<void>>();
  const { sitekey, enabled, displayBadge = false, pageId } = config;
  const analyticsClient = useAnalyticsClient();
  const scriptLogic = useScoreRecaptchaScripts({ sitekey });

  const sendTrackingEvent = useCallback(
    (action: TrackingEventAction, errorType?: TrackingEventErrorType) => {
      analyticsClient.trackingEvent({
        page: pageId,
        action: action,
        subject: 'score-recaptcha',
        ...(errorType && {
          attributes: {
            hasError: true,
            errorName: errorType,
          },
        }),
      });
    },
    [analyticsClient, pageId]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const initRecaptcha = async () => {
      try {
        await scriptLogic.injectRecaptchaScript();
        sendTrackingEvent('loaded-recaptcha');
        if (!displayBadge) {
          scriptLogic.hideRecaptchaBadge();
        }
        setIsLoaded(true);
      } catch (error) {
        const errorMessage = String(error);
        setLoadRecaptchaErrorReason(errorMessage);
        captureException(error);
        sendTrackingEvent('load-recaptcha-failed', 'recaptcha-script-error');
      }
    };

    scriptInitializationRef.current = initRecaptcha();

    return () => {
      scriptLogic.unloadRecaptcha();
      setLoadRecaptchaErrorReason(undefined);
      scriptInitializationRef.current = undefined;
    };
  }, [displayBadge, sitekey, pageId, enabled, analyticsClient, sendTrackingEvent, scriptLogic]);

  const fetchRecaptchaToken = useCallback(
    async (action: string) => {
      if (!enabled) {
        return '';
      }

      if (scriptInitializationRef.current) {
        await scriptInitializationRef.current;
      }

      if (!window?.grecaptcha?.enterprise?.execute) {
        captureRecaptchaException(
          RecaptchaError.fromMessage(RECAPTCHA_ENTERPRISE_EXECUTE_UNAVAILABLE),
          {},
          'no_token_score'
        );
        sendTrackingEvent('get-recaptcha-token', 'recaptcha-window-error');
        return '';
      }
      try {
        return await window.grecaptcha.enterprise.execute(sitekey, { action });
      } catch (err) {
        captureRecaptchaException(RecaptchaError.fromError(err), {}, 'no_token_score');
        sendTrackingEvent('get-recaptcha-token', 'recaptcha-script-error');
        return '';
      }
    },
    [enabled, sendTrackingEvent, sitekey]
  );

  return {
    isLoaded,
    fetchRecaptchaToken,
    loadRecaptchaErrorReason,
    enabled,
  };
};
